import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Landmark, Loader2, RefreshCw, Send, Ban } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/auth";

const CLOSED = ["2600", "3100"];

export const isFaceClosed = (face) =>
  CLOSED.includes(face?.tramitacion?.code) || face?.anulacion?.code === "4300";

const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" }) : "—";

const Row = ({ label, value, testId }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-zinc-100 last:border-0">
    <span className="text-zinc-500">{label}</span>
    <span className="font-medium text-zinc-900 text-right" data-testid={testId}>
      {value || "—"}
    </span>
  </div>
);

const Dir3Summary = ({ client }) => (
  <div className="rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-sm" data-testid="face-dir3-summary">
    <Row label="Oficina contable" value={client?.dir3_oficina_contable} />
    <Row label="Órgano gestor" value={client?.dir3_organo_gestor} />
    <Row label="Unidad tramitadora" value={client?.dir3_unidad_tramitadora} />
  </div>
);

const FaceStatus = ({ face }) => (
  <div className="rounded-sm border border-zinc-200 p-3 text-sm" data-testid="face-status-box">
    <Row label="Nº de registro" value={face.numero_registro} testId="face-registro" />
    <Row label="Recibida en FACe" value={face.fecha_recepcion} />
    <Row
      label="Estado"
      value={[face.tramitacion?.name, face.tramitacion?.reason].filter(Boolean).join(" · ")}
      testId="face-tramitacion"
    />
    <Row
      label="Anulación"
      value={[face.anulacion?.name, face.anulacion?.reason].filter(Boolean).join(" · ")}
      testId="face-anulacion"
    />
    <Row label="Última consulta" value={fmtDateTime(face.checked_at)} />
  </div>
);

export const FaceDialog = ({ invoice, client, onClose, onUpdated }) => {
  const [busy, setBusy] = useState("");
  const [confirming, setConfirming] = useState(false);
  const [cancelMode, setCancelMode] = useState(false);
  const [motivo, setMotivo] = useState("");
  const face = invoice?.face || {};
  const sent = !!face.numero_registro && !isFaceClosed(face);
  const canSend = !!client?.is_public_admin;

  const call = async (kind, url, body) => {
    setBusy(kind);
    try {
      const res = await adminApi.post(url, body);
      onUpdated(invoice.id, res.data);
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "FACe no ha respondido. Inténtalo de nuevo.");
      return false;
    } finally {
      setBusy("");
    }
  };

  const base = `/admin/invoicing/invoices/${invoice?.id}/face`;
  const send = async () => {
    if (await call("send", `${base}/send`)) toast.success("Factura registrada en FACe.");
    setConfirming(false);
  };
  const refresh = async () => {
    if (await call("status", `${base}/status`)) toast.success("Estado actualizado.");
  };
  const cancel = async () => {
    if (await call("cancel", `${base}/cancel`, { motivo })) {
      toast.success("Anulación solicitada.");
      setCancelMode(false);
      setMotivo("");
    }
  };

  return (
    <Dialog open={!!invoice} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="face-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5" /> FACe · Factura {invoice?.number}
          </DialogTitle>
          <DialogDescription>
            {client?.name || invoice?.client_snapshot?.name}. Se envía la
            e-factura firmada con tu certificado, que debe estar dado de alta
            en FACe (face.gob.es → Proveedores → Integradores).
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!canSend && !sent && (
            <p className="rounded-sm border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900" data-testid="face-not-public">
              Este cliente no está marcado como administración pública. Ábrelo
              en Clientes, márcalo y rellena sus códigos DIR3.
            </p>
          )}
          {canSend && !sent && <Dir3Summary client={client} />}
          {face.numero_registro && <FaceStatus face={face} />}
          {cancelMode && (
            <Textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Motivo de la anulación"
              rows={3}
              data-testid="face-cancel-reason"
            />
          )}
          {confirming && (
            <p className="text-sm font-semibold text-red-700" data-testid="face-confirm-warning">
              El envío es real: la factura quedará registrada en la
              administración. Pulsa «Confirmar envío» para enviarla.
            </p>
          )}
        </div>

        <DialogFooter className="gap-2 flex-wrap">
          <Button variant="outline" onClick={onClose} data-testid="face-close-btn">
            Cerrar
          </Button>
          {sent && !cancelMode && (
            <>
              <Button variant="outline" onClick={refresh} disabled={!!busy} data-testid="face-refresh-btn">
                {busy === "status" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Ver estado
              </Button>
              {face.anulacion?.code !== "4200" && (
                <Button
                  variant="outline"
                  onClick={() => setCancelMode(true)}
                  className="border-red-300 text-red-700 hover:bg-red-50"
                  data-testid="face-cancel-open-btn"
                >
                  <Ban className="h-4 w-4 mr-2" /> Pedir anulación
                </Button>
              )}
            </>
          )}
          {cancelMode && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  setCancelMode(false);
                  setMotivo("");
                }}
                data-testid="face-cancel-back-btn"
              >
                Volver
              </Button>
              <Button
                onClick={cancel}
                disabled={!motivo.trim() || !!busy}
                className="bg-red-700 hover:bg-red-800 text-white"
                data-testid="face-cancel-submit-btn"
              >
                {busy === "cancel" && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Enviar solicitud
              </Button>
            </>
          )}
          {!sent && canSend && (
            <Button
              onClick={confirming ? send : () => setConfirming(true)}
              disabled={!!busy}
              className="bg-zinc-950 hover:bg-zinc-800 text-white"
              data-testid="face-send-btn"
            >
              {busy === "send" ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {confirming ? "Confirmar envío" : "Enviar a FACe"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
